/***
 *
 *   TICKET STATS ROW
 *   Grid on StatCard. The maths live server-side (GET /api/tickets/stats,
 *   valetify-server/model/ticketStats.js) — see stats.tailwind.js for the segmented band this renders as, and why it
 *   bleeds out of the page gutter to sit flush under the header.
 *
 **********/

import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Stat, Skeleton } from 'components/lib';
import Style from './stats.tailwind';
import { useTranslate } from 'app/translations';
import { useApiContext } from 'app/api';
import { buildQueryParams } from 'utilities/params';

const formatDays = (value) => (value === null || value === undefined ? null : value.toFixed(2));
const formatChange = (value) => (value === null || value === undefined ? undefined : value.toFixed(1));

const timeZone = (() => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (err) {
        return undefined;
    }
})();

const localDay = () => new Date().toDateString();

/**
 * Local calendar day, re-evaluated every minute so "today" rolls over at
 * midnight even when nothing else on the page changes.
 */
function useLocalDay() {
    const [day, setDay] = useState(localDay);
    useEffect(() => {
        const timer = setInterval(() => setDay(localDay()), 60 * 1000);
        return () => clearInterval(timer);
    }, []);
    return day;
}

/**
 * @param params     Same filters as the board's ticket list.
 * @param refreshKey Changes whenever the board's list changes (refetch or
 *                   realtime), so the band follows the tickets on screen.
 */
export const TicketStats = ({ params, enabled = true, refreshKey }) => {
    const { _t } = useTranslate();
    const { callApi } = useApiContext();
    const day = useLocalDay();

    const query = useQuery(
        ['ticketStats', params, day],
        () => callApi({ url: `/api/tickets/stats${buildQueryParams({ ...params, tz: timeZone })}` }),
        { enabled, keepPreviousData: true },
    );

    const { refetch } = query;
    useEffect(() => {
        if (enabled && refreshKey) refetch();
    }, [refreshKey]);

    const stats = query.data?.data;
    const isLoading = query.status === 'loading' || (enabled && !stats && query.isFetching);

    if (isLoading || !stats || Array.isArray(stats)) {
        return (
            <div className={Style.skeletonGrid}>
                {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className={Style.skeletonCell}>
                        <Skeleton className={Style.skeletonBar} />
                    </div>
                ))}
            </div>
        );
    }

    return (
        <section className={Style.container}>
            <Stat
                className={Style.card}
                loading={false}
                label={_t('ticket.stats.number')}
                icon="clipboard"
                iconColor="pink"
                value={stats.active ?? 0}
            />
            <Stat
                className={Style.card}
                loading={false}
                label={_t('ticket.stats.new')}
                icon="trending-up"
                iconColor="blue"
                value={stats.newToday ?? 0}
            />
            <Stat
                className={Style.card}
                loading={false}
                label={_t('ticket.stats.completedToday')}
                icon="check"
                iconColor="green"
                value={stats.completedToday ?? 0}
            />
            <Stat
                className={Style.card}
                loading={false}
                label={_t('ticket.stats.averageCompletion')}
                icon="clock"
                iconColor="purple"
                // an em dash, not 0 — "nothing completed in the window" is not
                // the same claim as "tickets complete instantly"
                value={formatDays(stats.completion) ?? '—'}
                valueLabel={stats.completion === null ? undefined : _t('days')}
                change={formatChange(stats.completionChange)}
                lowerIsBetter
            />
            <Stat
                className={Style.card}
                loading={false}
                label={_t('ticket.stats.averagePending')}
                icon="clock"
                iconColor="orange"
                value={formatDays(stats.pending) ?? '—'}
                valueLabel={stats.pending === null ? undefined : _t('days')}
                change={formatChange(stats.pendingChange)}
                lowerIsBetter
            />
        </section>
    );
};
